import { ActionIcon, Button, Group, Paper, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconLock,
  IconRefresh,
  IconSearchOff,
  IconWifiOff,
} from "@tabler/icons-react";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { classifyError, ErrorKind } from "../../helpers/errors.helpers";

export type QueryErrorProps = {
  error?: unknown;
  type?: ErrorKind;
  onRetry?: () => void;
  size?: "section" | "inline";
  title?: string;
  isRetrying?: boolean;
  mih?: number | string;
};

const HEADLINE_KEYS = {
  unreachable: "common.queryError.unreachable.title",
  forbidden: "common.queryError.forbidden.title",
  notFound: "common.queryError.notFound.title",
  other: "common.queryError.other.title",
} as const;

const ICONS = {
  unreachable: IconWifiOff,
  forbidden: IconLock,
  notFound: IconSearchOff,
  other: IconAlertTriangle,
} as const;

function QueryError({
  error,
  type,
  onRetry,
  size = "section",
  title,
  isRetrying,
  mih,
}: QueryErrorProps) {
  const { t } = useTranslation();
  const kind = type ?? classifyError(error);
  const headline = title ?? t(HEADLINE_KEYS[kind]);
  const Icon = ICONS[kind];
  const retryable = !!onRetry && (kind === "unreachable" || kind === "other");
  const handleRetry = () => {
    onRetry?.();
  };

  if (size === "inline") {
    return (
      <Group gap={4} wrap="nowrap" display="inline-flex" align="center">
        <Icon size={16} color="var(--mantine-color-bluegray-5)" />
        <Text size="sm" c="bluegray.5">
          {headline}
        </Text>
        {retryable && (
          <ActionIcon
            variant="subtle"
            color="bluegray"
            size="sm"
            aria-label={t("common.queryError.retry")}
            loading={isRetrying}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              // Inline errors are rendered inside table/row links (e.g. the
              // groups list), so the retry click must not navigate.
              e.preventDefault();
              e.stopPropagation();
              onRetry?.();
            }}
          >
            <IconRefresh size={14} />
          </ActionIcon>
        )}
      </Group>
    );
  }

  let subtext: string | null = null;
  if (kind === "unreachable") {
    subtext = t("common.queryError.unreachable.description");
  } else if (title) {
    subtext = t(HEADLINE_KEYS[kind]);
  }

  return (
    <Paper
      bg="bluegray.1"
      radius="md"
      p="xl"
      mih={mih ?? 300}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
      }}
    >
      <Icon size={32} color="var(--mantine-color-bluegray-5)" />
      <Text fw="bold" ta="center" c="bluegray.5">
        {headline}
      </Text>
      {subtext && (
        <Text c="dimmed" ta="center" size="sm">
          {subtext}
        </Text>
      )}
      {retryable && (
        <Button
          variant="light"
          color="bluegray"
          size="sm"
          loading={isRetrying}
          onClick={handleRetry}
        >
          {t("common.queryError.retry")}
        </Button>
      )}
    </Paper>
  );
}

export default QueryError;
